/**
 * Admin · Entitlements. SUPER-ADMIN gets a per-plan editor (every tier's feature
 * values, editable + saved via `PATCH /api/admin/entitlements/{tier}`); other admins
 * see their own tier's flags read-only. Entitlements are the ceiling that caps the
 * customer's resolved options (e.g. the `max_views` cap in the Editor → Options system).
 */
import { useCallback, useEffect, useState } from "react";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import { Accordion } from "../ui/Accordion.js";
import { ActionBar } from "../ui/ActionBar.js";
import { useSessionStore } from "../stores/session.js";
import { cacheEpoch, readCache, writeCache } from "../stores/cache.js";
// WS-B B-3: the tier keys and feature keys are WIRE identifiers. They stay raw in every
// `data-testid`, `key` and save payload; only what a person READS goes through `enumLabel`.
import { enumLabel } from "../format/labels.js";
import type { EntitlementsByTier, Entitlements } from "@genz/platform";

/** Cache key — seeds the per-tier editor instantly on tab re-open (cache-first). */
const ENTITLEMENTS_CACHE = "admin.entitlements";

type FeatureVal = boolean | number | string | null;

/**
 * P0-5: the editor picked its input type from `typeof value`, so a NUMERIC feature whose
 * current value is `null` (e.g. `enterprise.ai_monthly_limit`, "unlimited") rendered a TEXT
 * box. Whatever was typed was stored verbatim as a STRING, and the server's `int > "5000"`
 * comparison then threw on EVERY generation for that tier.
 *
 * A feature's kind is a property of the FEATURE, not of one tier's current value: infer it
 * from the same feature across the other tiers (free/pro carry real numbers where enterprise
 * carries null), and fall back to the name for the all-null case. Only when nothing says
 * otherwise is it free text.
 */
type FeatureKind = "boolean" | "number" | "text";
const NUMERIC_NAME = /(_limit|_max|max_|_cap|cap_|_px|_count|_days|per_)/;

function featureKind(tiers: EntitlementsByTier, feature: string, value: FeatureVal): FeatureKind {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  for (const features of Object.values(tiers)) {
    const other = (features as Record<string, FeatureVal>)[feature];
    if (typeof other === "boolean") return "boolean";
    if (typeof other === "number") return "number";
  }
  return NUMERIC_NAME.test(feature) ? "number" : "text";
}

/** Coerce an edited value to its feature's kind before it goes on the wire (also repairs a
 *  string a previous build — or a cached payload — left behind). */
function coerce(kind: FeatureKind, value: FeatureVal): FeatureVal {
  if (kind !== "number" || value == null || typeof value === "number") return value;
  const n = Number(value);
  return String(value).trim() === "" || Number.isNaN(n) ? null : n;
}

function renderValue(v: FeatureVal) {
  if (typeof v === "boolean") {
    return <span className={`badge ${v ? "badge--success" : "badge--neutral"}`}>{v ? "on" : "off"}</span>;
  }
  return <span className="badge badge--info">{String(v)}</span>;
}

export function EntitlementsTab() {
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  return isSuper ? <EntitlementsEditor /> : <EntitlementsReadOnly />;
}

function EntitlementsEditor() {
  const { client } = useServices();
  const [tiers, setTiers] = useState<EntitlementsByTier | null>(
    () => readCache<EntitlementsByTier>(ENTITLEMENTS_CACHE),
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* The open plan (Z0 fix round F-6, controlled `Accordion`).
     THREE states, not two. `undefined` is "the editor has not picked one yet" and opens the
     head of the live plan list; `null` is "they closed the one that was open" and really does
     leave none open, which is the primitive's own contract; a name that is no longer a plan
     falls back to the head. Two states cannot tell the first from the second, and the first
     plan springs back open under the pointer that just closed it. */
  const [open, setOpen] = useState<string | null | undefined>(undefined);

  const load = useCallback(async () => {
    setError(null);
    const epoch = cacheEpoch();
    try {
      const fresh = await client.adminListEntitlements();
      writeCache(ENTITLEMENTS_CACHE, fresh, epoch);
      setTiers(fresh);
    } catch (e) {
      // Keep showing the cached editor when offline; only error with nothing cached.
      if (readCache<EntitlementsByTier>(ENTITLEMENTS_CACHE) == null) {
        setError(e instanceof Error ? e.message : String(e));
      }
    }
  }, [client]);

  useEffect(() => { void load(); }, [load]);

  function edit(tier: string, feature: string, value: FeatureVal) {
    setTiers((t) => (t ? { ...t, [tier]: { ...t[tier], [feature]: value } } : t));
  }

  async function save(tier: string) {
    if (!tiers) return;
    setBusy(tier);
    setError(null);
    try {
      // Belt and braces: coerce every numeric feature on the way out, so a value that
      // arrived as a string (cache written by an older build, or a hand-edited payload)
      // cannot be persisted as one and 500 the tier's generations.
      const features = Object.fromEntries(
        Object.entries(tiers[tier]).map(([f, v]) =>
          [f, coerce(featureKind(tiers, f, v as FeatureVal), v as FeatureVal)]),
      ) as typeof tiers[string];
      await client.adminUpdateTierEntitlements(tier, features);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!tiers) return <p className="faint" data-testid="entitlements-loading">Loading…</p>;

  return (
    <section className="admin-tab" data-testid="admin-entitlements">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title panel-title">Entitlements <InfoHint text="Manage each plan's feature flags. These cap what a plan can use." /></strong>
        </div>
      </div>
      {error && <p role="alert" data-testid="entitlements-error">{error}</p>}
      {/* Rule 8. Each plan is a fieldset with its own `Save {tier}` — three plans, three
          `.btn--primary` on one screen, which is the One-Primary rule broken three ways by a
          `.map()`. The plans are an accordion now: one open at a time, so one Save is on
          screen, and the per-plan commit (one PUT per tier, `adminUpdateTierEntitlements`)
          is untouched. Every `{tier}-{feature}` and `{tier}-save` testid survives.
          CONTROLLED (Z0 fix round F-6). `defaultOpen` is read ONCE, at mount, and this
          accordion mounts on the render that `tiers` arrives — so a later reload that returns
          a different first plan left the open section pointing at a plan that is no longer
          there. The open key is owned here and filtered through the live plan list. */}
      <Accordion
        name="admin-entitlements"
        openKey={
          open != null && open in tiers ? open : open === null ? null : (Object.keys(tiers)[0] ?? null)
        }
        onOpenChange={setOpen}
        items={Object.entries(tiers).map(([tier, features]) => ({
          key: tier,
          head: enumLabel(tier),
          body: (
            <fieldset data-testid={`tier-${tier}`}>
              <legend className="admin-sr-only">{enumLabel(tier)}</legend>
              <div className="settings__grid">
                {Object.entries(features).map(([feature, value]) => {
                  const kind = featureKind(tiers, feature, value as FeatureVal);
                  return (
                  <label className="field" key={feature}>
                    <span>{enumLabel(feature)}</span>
                    {kind === "boolean" ? (
                      <input type="checkbox" checked={value === true}
                        data-testid={`${tier}-${feature}`}
                        onChange={(e) => edit(tier, feature, e.target.checked)} />
                    ) : kind === "number" ? (
                      // Numeric even when the stored value is null ("unlimited") — an empty
                      // box means null, never the string "".
                      <input type="number" value={value == null ? "" : String(value)}
                        placeholder="unlimited"
                        data-testid={`${tier}-${feature}`}
                        onChange={(e) => edit(tier, feature,
                          e.target.value === "" ? null : Number(e.target.value))} />
                    ) : (
                      <input type="text" value={value == null ? "" : String(value)}
                        placeholder={value == null ? "unlimited" : ""}
                        data-testid={`${tier}-${feature}`}
                        onChange={(e) => edit(tier, feature, e.target.value === "" ? null : e.target.value)} />
                    )}
                  </label>
                  );
                })}
              </div>
              <ActionBar
                label={`${enumLabel(tier)} plan actions`}
                primary={
                  <button type="button" className="btn btn--primary" disabled={busy === tier}
                    data-testid={`${tier}-save`} onClick={() => void save(tier)}>
                    {busy === tier ? "Saving…" : `Save ${enumLabel(tier)}`}
                  </button>
                }
              />
            </fieldset>
          ),
        }))}
      />
    </section>
  );
}

function EntitlementsReadOnly() {
  const { client } = useServices();
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try { setEnt(await client.entitlements()); }
      catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    })();
  }, [client]);

  const features = ent ? Object.entries(ent.features) : [];
  return (
    <section className="admin-tab" data-testid="admin-entitlements">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title">Entitlements</strong>
          <p className="admin-panel__sub">Your plan's module/feature flags (read-only).</p>
        </div>
      </div>
      <p className="admin-tab__note" data-testid="entitlements-readonly-note">
        Entitlements are managed server-side (per tier). This view is read-only.
      </p>
      {error && <p role="alert">{error}</p>}
      {ent && (
        <>
          <div className="admin-tab__stats">
            <span className="faint">Tier</span>
            <span className="badge badge--info" data-testid="entitlements-tier">{enumLabel(ent.tier)}</span>
          </div>
          <div className="table-scroll">
          <table className="table" data-testid="entitlements-table"><thead>
            <tr><th>Feature</th><th>Value</th></tr></thead><tbody>
            {features.map(([key, value]) => (
              <tr key={key} data-testid="entitlement-row">
                <td>{enumLabel(key)}</td><td>{renderValue(value as FeatureVal)}</td>
              </tr>
            ))}
          </tbody></table>
          </div>
        </>
      )}
    </section>
  );
}
